import React, { useState, useEffect } from 'react';
import {
  createUser,
  getEstabelecimentos,
  getPermissionProfiles,
  getUserById,
  updateUser,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  FormControlLabel,
  Switch,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material';

function FormUsuario({
  isEditMode = false,
  userId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const id = userId ?? params.id;
  const editing = isEditMode || !!userId;

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [senha, setSenha] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [considerarVendedor, setConsiderarVendedor] = useState(true);
  const [receberAlertasWhatsApp, setReceberAlertasWhatsApp] = useState(false);
  const [perfilId, setPerfilId] = useState('');
  const [percentualComissao, setPercentualComissao] = useState('0');
  const [perfis, setPerfis] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [estabelecimentosIds, setEstabelecimentosIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const perfilSelecionado = perfis.find((p) => Number(p.id) === Number(perfilId));
  const acessoTodasFiliais = Boolean(
    perfilSelecionado?.acessoTodasFiliais || perfilSelecionado?.acessoGerencial,
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [profilesResponse, estabsResponse] = await Promise.all([
          getPermissionProfiles(),
          getEstabelecimentos(),
        ]);
        setPerfis(profilesResponse.data || []);
        const listaEstabs = Array.isArray(estabsResponse.data) ? estabsResponse.data : [];
        setFiliais(listaEstabs.map((e) => ({
          id: e.id,
          nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
        })));

        if (!editing && profilesResponse.data?.length) {
          setPerfilId(profilesResponse.data[0].id);
        }

        if (editing) {
          const response = await getUserById(id);
          const usuario = response.data;
          setNome(usuario.nome || '');
          setEmail(usuario.email || '');
          setTelefone(usuario.telefone || '');
          setAtivo(usuario.ativo);
          setConsiderarVendedor(usuario.considerarVendedor);
          setReceberAlertasWhatsApp(Boolean(usuario.receberAlertasWhatsApp));
          setPerfilId(usuario.perfilId);
          setPercentualComissao(String(usuario.percentualComissao ?? 0));
          setEstabelecimentosIds(
            (usuario.estabelecimentosIds || []).map((eid) => Number(eid)).filter((eid) => eid > 0),
          );
        }
      } catch (error) {
        setErrorMessage(getApiErrorMessage(error, 'Não foi possível carregar o usuário.'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, editing]);

  useEffect(() => {
    if (acessoTodasFiliais) {
      setEstabelecimentosIds([]);
    }
  }, [acessoTodasFiliais, perfilId]);

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/users');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!acessoTodasFiliais && estabelecimentosIds.length === 0) {
      setErrorMessage('Selecione ao menos uma filial permitida para este usuário.');
      return;
    }

    const usuarioData = {
      nome,
      email,
      telefone,
      senha: senha || null,
      ativo,
      considerarVendedor,
      receberAlertasWhatsApp,
      perfilId: Number(perfilId),
      percentualComissao: Number(String(percentualComissao).replace(',', '.')),
      estabelecimentosIds: acessoTodasFiliais ? [] : estabelecimentosIds,
    };

    try {
      if (editing) {
        await updateUser(id, usuarioData);
      } else {
        await createUser(usuarioData);
      }
      if (onSaved) onSaved(editing ? 'Usuário atualizado' : 'Usuário criado');
      else navigate('/users');
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setErrorMessage("Você não tem permissão para realizar esta ação.");
      } else {
        setErrorMessage(getApiErrorMessage(error, 'Não foi possível salvar o usuário.'));
      }
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 900, margin: 'auto' }}>
      <Card
        elevation={embedded ? 0 : 1}
        sx={embedded ? { bgcolor: 'transparent', boxShadow: 'none' } : undefined}
      >
        <CardContent sx={{ p: embedded ? 0 : 2, '&:last-child': { pb: embedded ? 0 : 2 } }}>
          {!embedded && (
            <Typography variant="h5" gutterBottom>
              {editing ? "Editar Usuário" : "Criar Usuário"}
            </Typography>
          )}

          {/* Exibe a mensagem de erro se houver */}
          {errorMessage && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
          <form onSubmit={handleSave}>
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Telefone"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Grupo de permissões"
                    value={perfilId}
                    onChange={(e) => setPerfilId(e.target.value)}
                    fullWidth
                    required
                  >
                    {perfis.map((perfil) => (
                      <MenuItem key={perfil.id} value={perfil.id}>
                        {perfil.descricao}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Percentual de comissão"
                    value={percentualComissao}
                    onChange={(e) => setPercentualComissao(e.target.value)}
                    inputMode="decimal"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    disabled={acessoTodasFiliais}
                    options={filiais}
                    getOptionLabel={(opt) => opt.nome}
                    isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val.id)}
                    value={filiais.filter((f) => estabelecimentosIds.includes(Number(f.id)))}
                    onChange={(_, values) => {
                      setEstabelecimentosIds(values.map((v) => Number(v.id)));
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.nome}
                          size="small"
                          {...getTagProps({ index })}
                          key={option.id}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filiais permitidas"
                        placeholder={acessoTodasFiliais ? 'Acesso a todas as filiais' : 'Selecione'}
                        helperText={
                          acessoTodasFiliais
                            ? 'O grupo selecionado tem acesso a todas as filiais.'
                            : 'Restringe vendas, estoque e relatórios às filiais escolhidas.'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                fullWidth
                placeholder={editing ? "Digite nova senha se deseja alterar" : ""}
                required={!editing}
              />

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Status e operação</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
                      }
                      label={ativo ? 'Usuário ativo' : 'Usuário inativo'}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={considerarVendedor}
                          onChange={(e) => setConsiderarVendedor(e.target.checked)}
                        />
                      }
                      label="Considerar como vendedor"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={receberAlertasWhatsApp}
                          onChange={(e) => setReceberAlertasWhatsApp(e.target.checked)}
                        />
                      }
                      label="Receber alertas no WhatsApp"
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Usuários inativos não conseguem entrar no ZenGestão nem operar o PDV.
                    As permissões são definidas pelo grupo selecionado.
                    Alertas no WhatsApp usam o telefone deste cadastro.
                  </Typography>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button color="inherit" variant="outlined" onClick={fechar}>
                  Cancelar
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {editing ? "Salvar Alterações" : "Criar Usuário"}
                </Button>
              </Stack>
            </Stack>
          </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default FormUsuario;
